import React, { useEffect, useState } from "react";
import Table from "@ingka/table";
import Select from "@ingka/select";
import { SkapaPromptModal, ModalOption } from "../../../modals/SkapaSheetModal";
import { CreateUserType } from "../types/users.types";
import { UserModalChildren, AllCountries, AllRoles, AllStoresByCountry } from "./users";
import { UsersTableLabels } from "../molecules/UsersTableLabels";
import { isUserCountrySuperUser, isUserGlobalSuperUser } from "../../../utils/roles.helpers";
import { useLoggedInUser } from "../hooks/useLoggedInUser";
import Search from "@ingka/search";
import { TableToolsHeader } from "../../layout/TableToolsHeader";
import { USER_ROLES, UserRole } from "../../../types/roles.types";
import { isChineseEnvironment } from "../../../services/shared.service";
import { SkapaToast } from "../../SkapaToast";
import styled from "styled-components";
import { InlineMessageBox } from "../../layout/InlineMessageBox";
import { FixaInlineMessage } from "../../FixaInlineMessage";

const HeaderControlsContainer = styled.div`
  display: flex;
  align-items: center;
`;

const InlineMessageContainer = styled.div`
  margin-right: 1rem;
`;

export const UsersPage = () => {
  const { user } = useLoggedInUser();
  const [country, setCountry] = useState<string>("");
  const [store, setStore] = useState<string>("");
  const [role, setRole] = useState<string>("SUPERUSER");
  const [userState, setUserState] = useState<CreateUserType>();
  const [isGSU, setIsGSU] = useState(false);
  const [isCSU, setIsCSU] = useState(false);
  const [isToastOpen, setIsToastOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [searchInput, setSearchInput] = useState<string>("");
  const [searchValue, setSearchValue] = useState<string>("");
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const StyledInlineMessageBox = styled(InlineMessageBox)`
    position: absolute;
    bottom: 80px;
  `;
  useEffect(() => {
    if (!user) {
      return;
    }
    setCountry(user.countryId);
    setStore(user.storeId);
    setUserState(user);
    setIsGSU(isUserGlobalSuperUser(user.roleId));
    setIsCSU(isUserCountrySuperUser(user.roleId));
  }, [user]);

  const handleSearchClick = () => {
    if (searchInput.length < 3) {
      return;
    }
    setSearchValue(searchInput);
  };

  const onClear = () => {
    setSearchInput("");
    setSearchValue("");
  };

  React.useEffect(() => {
    if (searchInput.length < 1) {
      onClear();
    }
  }, [searchValue, searchInput]);

  return (
    <>
      {userState && (
        <>
          <TableToolsHeader
            setShowTeamUsersTable={() => {}}
            showTeamUsersTable={false}
            selectedTeam={{ id: "", name: "" }}
            tableTitle="All users"
            headerControls={
              <HeaderControlsContainer>
                <InlineMessageContainer>
                  <FixaInlineMessage
                    type="information"
                    title={
                      "Adding a regular INGKA store co-worker is no longer needed - just have the user log in to the app!"
                    }
                    body={"If the user is having login issues, please have them create a NowIT ticket."}
                  />
                </InlineMessageContainer>
                <SkapaPromptModal buttonText={"Add user"} description={""} option={ModalOption.ADD} onClose={() => {}}>
                  <UserModalChildren
                    country={country}
                    user={userState}
                    setUserHandler={setUserState}
                    modalType={ModalOption.ADD}
                    setModalVisible={() => {}}
                    setSuccessMessage={(message: string | ((prevState: string) => string)) => {
                      setToastMessage(message);
                      setIsToastOpen(true);
                    }}
                  />

                  {isToastOpen && (
                    <SkapaToast text={toastMessage} isOpen={isToastOpen} onCloseRequest={() => setIsToastOpen(false)} />
                  )}
                </SkapaPromptModal>
              </HeaderControlsContainer>
            }
            filtersLeft={
              <>
                {!isChineseEnvironment() && (
                  <Select
                    id={"countryselect"}
                    label={"Country"}
                    value={country}
                    children={<AllCountries />}
                    onChange={(event: React.ChangeEvent<HTMLSelectElement>) => {
                      setStore("");
                      setCountry(event.target.value);
                    }}
                    disabled={!isGSU}
                  />
                )}
                <Select
                  id={"storeselect"}
                  label={"Store"}
                  children={<AllStoresByCountry country={country} />}
                  onChange={(event: React.ChangeEvent<HTMLSelectElement>) => setStore(event.target.value)}
                  disabled={!isCSU && !isGSU}
                />
                <Select
                  id={"roleselect"}
                  label={"Role"}
                  children={<AllRoles />}
                  onChange={(event: React.ChangeEvent<HTMLSelectElement>) =>
                    USER_ROLES.includes(event.target.value as UserRole) ? setRole(event.target.value) : setRole("")
                  }
                />
              </>
            }
            filtersRight={
              <>
                {isSearchFocused && searchInput.length < 3 && (
                  <StyledInlineMessageBox body="3 characters required" variant="cautionary" subtle />
                )}
                <Search
                  id={"searchuser"}
                  type={"text"}
                  className={"search"}
                  placeholder={"Find user by name"}
                  onClear={() => {
                    setSearchInput("");
                    setSearchValue("");
                  }}
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.currentTarget.value)}
                  onSearch={() => handleSearchClick()}
                  onFocus={() => setIsSearchFocused(true)}
                  onBlur={() => setIsSearchFocused(false)}
                ></Search>
              </>
            }
          />
          <Table style={{ zIndex: 1 }} fullWidth inset>
            <UsersTableLabels country={country} store={store} role={role} searchValue={searchValue} onSort={() => {}} />
          </Table>
        </>
      )}
    </>
  );
};
